<template>
	<div
		ref="mainElem"
		class="c-DateTimePicker"
		:class="dateTimePickerClassObj">
		<VueCtkDateTimePicker
			:id="datePickerId"
			v-model="model"
			:label="label"
			:no-keyboard="disabled"
			:disabled="disabled"
			:only-time="onlyTime"
			:only-date="onlyDate"
			:format="inputFormat"
			:formatted="fieldFormat"
			:output-format="outputFormat"
			:min-date="minDate"
			:max-date="maxDate"
			color="#3843D0"
			@input="handleInput">
		</VueCtkDateTimePicker>
	</div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

export default {
	name: 'DateTimePicker',
	components: {
		VueCtkDateTimePicker
	},
	props: {
		/**
     * The DateTimePicker data field
     */
		data: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The DateTimePicker label
     */
		label: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The DateTimePicker input format (momentJS style)
     */
		inputFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DDTHH:mm:ss.SSSZ'
		},
		/**
     * The DateTimePicker display format (momentJS style)
     */
		fieldFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DD HH:mm'
		},
		/**
     * The DateTimePicker output format (momentJS style)
     */
		outputFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DDTHH:mm:ss'
		},
		/**
     * If the DateTimePicker should be for dates only
     * @values true, false
     */
		onlyDate: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the DateTimePicker should be for time only
     * @values true, false
     */
		onlyTime: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * The DateTimePicker MIN date (use the same format as fieldFormat)
     */
		minDate: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * The DateTimePicker MAX date (use the same format as fieldFormat)
     */
		maxDate: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * If the DateTimePicker is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the DateTimePicker is disabled
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			datePickerId: this.randomizeId(),
			model: undefined
		}
	},
	computed: {
		dateTimePickerClassObj () {
			return {
				disabled: this.disabled,
				isActive: this.isActive
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				this.model = newVal
			},
			immediate: true
		}
	},
	mounted () {
		this.$refs.mainElem.addEventListener('focus', this.handleFocus, true)
	},
	beforeDestroy () {
		this.$refs.mainElem.removeEventListener('focus', this.handleFocus, true)
	},
	methods: {
		randomizeId () {
			return `datepickerid${Math.floor(Math.random() * 100000000000000000000)}`
		},
		handleInput (val) {
			/**
       * Triggers when DateTimePicker changes it's value
       * @event change
       * @property {string} val new value set
       */
			this.$emit('change', val)
		},
		handleFocus (ev) {
			if (this.disabled) {
				ev.preventDefault()
				ev.stopPropagation()
			}
		}
	}
}
</script>

<style lang="scss">
@import '../../scss/variables.scss';

.c-DateTimePicker {
  $font-size-value: 16px;
  position: relative;

  &.disabled {

    .field-input {
      border-color: $un-n500 !important;
      background-color: $un-n100 !important;
      color: $un-n500 !important;
			cursor: not-allowed !important;

      &::-webkit-input-placeholder,
      &::-moz-placeholder,
      &::placeholder {
        color: $un-n500 !important;
      }
    }

    label {
      color: $un-n500 !important;
    }
  }

	&.isActive {
		.date-time-picker {

			.field .field-input {
				border-color: $un-blue;
				box-shadow: inset 0 0 0 1px $un-blue;

				&::-webkit-input-placeholder,
				&::-moz-placeholder,
				&::placeholder {
					color: $un-blue;
					opacity: 1;
				}
			}

			label {
				color: $un-blue;
			}
    }
	}

  .date-time-picker {
    height: 50px;
    font-family: $primary-font !important;

    .field {
      height: 100%;

      &.is-focused .field-input {
        box-shadow: inset 0 0 0 1px $un-blue;
      }
    }

    .field-input {
      height: 100%;
      border: 1px solid $un-gray-light;
      border-radius: $border-radius;
      background-color: $un-white;
      font-family: $primary-font;
      font-size: $font-size-value;
      line-height: $font-size-value;
      color: $un-n700;
			box-sizing: border-box;
			padding-top: 4px;

      &::-webkit-input-placeholder,
      &::-moz-placeholder,
      &::placeholder {
        color: $un-n700;
        opacity: 1;
      }
    }

    label {
      color: $un-n700;
      font-family: $primary-font;
      top: 8px;
    }
  }
}
</style>
